import { useState, useEffect, useRef } from 'react';

import StaffManagement from './components/StaffManagement';
import { useAuth } from '@/hooks/useAuth';
import { fixSyncIssues } from '@/hooks/useSupabaseStore';
import { useShop } from '@/hooks/useShop';
import { useCapital } from '@/hooks/useCapital';

export default function SettingsPage() {
  const { isAdmin, user, displayName, updateDisplayName } = useAuth();
  const [activeTab, setActiveTab] = useState<'general' | 'staff'>('general');

  const { shop, saveShop, uploadLogo } = useShop();
  const { totalCapital, capitalLogs, addTransaction, setCapital } = useCapital();

  const [fixLoading, setFixLoading] = useState(false);
  const [fixResult, setFixResult] = useState<{ fixedLoans: number; fixedCustomers: number; errors: string[] } | null>(null);

  // Lắng nghe event chuyển tab từ TopBar dropdown
  useEffect(() => {
    const handleSwitchTab = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail === 'staff' && isAdmin) {
        setActiveTab('staff');
      }
    };
    window.addEventListener('switch-settings-tab', handleSwitchTab);
    return () => window.removeEventListener('switch-settings-tab', handleSwitchTab);
  }, [isAdmin]);

  // ─── FORM STATE — khởi tạo từ shop trong DB ───
  const [shopName, setShopName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [printName, setPrintName] = useState('');
  const [printAddress1, setPrintAddress1] = useState('');
  const [printAddress2, setPrintAddress2] = useState('');
  const [interestRate, setInterestRate] = useState('3');
  const [loanDays, setLoanDays] = useState('30');
  const [remindDays, setRemindDays] = useState('3');
  const [maxLoanRatio, setMaxLoanRatio] = useState('75');
  const [emailNotif, setEmailNotif] = useState(true);
  const [autoRemind, setAutoRemind] = useState(true);
  const [autoBackup, setAutoBackup] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [warehouses, setWarehouses] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState('');
  const [newWarehouse, setNewWarehouse] = useState('');

  // Tài khoản cá nhân
  const [myName, setMyName] = useState('');
  const [nameSaved, setNameSaved] = useState(false);
  const [nameError, setNameError] = useState('');

  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [logoMsg, setLogoMsg] = useState('');
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Fill form 1 lần khi shop load xong (tránh ghi đè khi đang gõ)
  const inited = useRef(false);
  useEffect(() => {
    if (shop && !inited.current) {
      inited.current = true;
      setShopName(shop.name);
      setPhone(shop.phone);
      setAddress(shop.address);
      setPrintName(shop.printName);
      setPrintAddress1(shop.printAddress1);
      setPrintAddress2(shop.printAddress2);
      setInterestRate(String(shop.defaultInterestRate));
      setLoanDays(String(shop.defaultLoanDays));
      setRemindDays(String(shop.remindBeforeDays));
      setMaxLoanRatio(String(shop.maxLoanRatio));
      setEmailNotif(shop.emailNotif);
      setAutoRemind(shop.autoRemind);
      setAutoBackup(shop.autoBackup);
      setCategories(shop.loanCategories?.length ? shop.loanCategories : []);
      setWarehouses(shop.warehouses?.length ? shop.warehouses : []);
    }
  }, [shop]);

  const nameInited = useRef(false);
  useEffect(() => {
    if (displayName && !nameInited.current) {
      nameInited.current = true;
      setMyName(displayName);
    }
  }, [displayName]);

  const handleSaveName = async () => {
    setNameError('');
    const { error } = await updateDisplayName(myName);
    if (error) {
      setNameError(error.message);
    } else {
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 2500);
    }
  };

  const [capitalDisplay, setCapitalDisplay] = useState('');
  const capitalInited = useRef(false);
  useEffect(() => {
    if (!capitalInited.current && totalCapital > 0) {
      capitalInited.current = true;
      setCapitalDisplay(totalCapital.toLocaleString('vi-VN'));
    }
  }, [totalCapital]);

  const [capType, setCapType] = useState<'deposit' | 'withdraw'>('deposit');
  const [capAmount, setCapAmount] = useState('');
  const [capNote, setCapNote] = useState('');
  const [capError, setCapError] = useState('');
  const [capSuccess, setCapSuccess] = useState(false);

  const handleSave = async () => {
    setSaveError('');
    setSaving(true);
    const { error } = await saveShop({
      name: shopName,
      phone,
      address,
      printName,
      printAddress1,
      printAddress2,
      defaultInterestRate: Number(interestRate) || 0,
      defaultLoanDays: Number(loanDays) || 30,
      remindBeforeDays: Number(remindDays) || 3,
      maxLoanRatio: Number(maxLoanRatio) || 75,
      emailNotif,
      autoRemind,
      autoBackup,
      loanCategories: categories,
      warehouses,
    });
    if (error) {
      setSaveError(error.message);
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
    setSaving(false);
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoMsg('Đang tải logo...');
    // Chưa có shop → lưu thông tin cơ bản trước để có shop.id
    if (!shop) {
      const { error } = await saveShop({ name: shopName || 'Cửa hàng của tôi', phone, address });
      if (error) {
        setLogoMsg('Lỗi: ' + error.message);
        return;
      }
    }
    const { error } = await uploadLogo(file);
    setLogoMsg(error ? 'Lỗi: ' + error.message : 'Đã cập nhật logo!');
    setTimeout(() => setLogoMsg(''), 3000);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleCapitalTransaction = async () => {
    setCapError('');
    const amount = Number(capAmount.replace(/\D/g, ''));
    if (!amount || amount <= 0) {
      setCapError('Vui lòng nhập số tiền hợp lệ');
      return;
    }
    const { error } = await addTransaction(capType, amount, capNote.trim());
    if (error) {
      setCapError(error.message);
      return;
    }
    setCapitalDisplay((totalCapital + (capType === 'deposit' ? amount : -amount)).toLocaleString('vi-VN'));
    setCapAmount('');
    setCapNote('');
    setCapSuccess(true);
    setTimeout(() => setCapSuccess(false), 2500);
  };

  // Cập nhật tổng vốn trực tiếp
  const handleCapitalDisplayChange = async (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    setCapitalDisplay(digits ? Number(digits).toLocaleString('vi-VN') : '');
    await setCapital(Number(digits) || 0);
  };

  const handleFixSync = async () => {
    setFixLoading(true);
    setFixResult(null);
    try {
      const result = await fixSyncIssues();
      setFixResult(result);
    } catch (e) {
      setFixResult({ fixedLoans: 0, fixedCustomers: 0, errors: [(e as Error).message] });
    }
    setFixLoading(false);
  };

  const Toggle = ({ value, onChange }: { value: boolean; onChange: () => void }) => (
    <button onClick={onChange} className={`relative w-11 h-6 rounded-full transition-colors duration-200 cursor-pointer ${value ? 'bg-[#f59e0b]' : 'bg-gray-200'}`}>
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${value ? 'translate-x-5' : 'translate-x-0.5'}`}></span>
    </button>
  );

  return (<>
      <div className="max-w-3xl space-y-5">
        {/* Tabs */}
        {isAdmin && (
          <div className="bg-white rounded-2xl p-1.5 flex gap-1">
            <button
              onClick={() => setActiveTab('general')}
              className={`flex-1 px-4 py-2 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'general'
                  ? 'bg-amber-50 text-[#f59e0b]'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-settings-3-line text-base"></i>
                </span>
                Cài đặt chung
              </span>
            </button>
            <button
              onClick={() => setActiveTab('staff')}
              className={`flex-1 px-4 py-2 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'staff'
                  ? 'bg-amber-50 text-[#f59e0b]'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-team-line text-base"></i>
                </span>
                Quản lý nhân viên
              </span>
            </button>
          </div>
        )}

        {activeTab === 'general' && (
          <>
            {/* My Account */}
            <div className="bg-white rounded-2xl p-6">
              <h3 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-sky-100 rounded-lg text-sky-600"><i className="ri-user-settings-line text-base"></i></span>
                Tài Khoản Của Tôi
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Email đăng nhập</label>
                  <input value={user?.email || ''} disabled className="w-full px-3 py-2 border border-gray-100 rounded-lg text-sm bg-gray-50 text-gray-400 cursor-not-allowed" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Tên hiển thị</label>
                  <input value={myName} onChange={e => setMyName(e.target.value)} placeholder="Tên hiển thị trên hệ thống" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors" />
                </div>
              </div>
              <div className="flex items-center gap-3 mt-4">
                <button onClick={handleSaveName} className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-semibold hover:bg-gray-900 transition-colors cursor-pointer whitespace-nowrap">
                  Cập nhật tên
                </button>
                {nameSaved && (
                  <div className="flex items-center gap-1.5 text-emerald-600 text-sm font-medium">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-checkbox-circle-line text-sm"></i></span>
                    Đã cập nhật!
                  </div>
                )}
                {nameError && <p className="text-xs text-red-500">{nameError}</p>}
              </div>
            </div>

            {/* Shop Info */}
            <div className="bg-white rounded-2xl p-6">
              <h3 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-lg text-amber-600"><i className="ri-store-2-line text-base"></i></span>
                Thông Tin Cửa Hàng
              </h3>
              {/* Logo */}
              <div className="flex items-center gap-4 mb-5 pb-5 border-b border-gray-100">
                <div className="w-16 h-16 rounded-xl bg-[#1a2332] flex items-center justify-center overflow-hidden shrink-0">
                  {shop?.logoUrl ? (
                    <img src={shop.logoUrl} alt="Logo tiệm" className="w-full h-full object-contain" />
                  ) : (
                    <i className="ri-store-2-line text-2xl text-amber-400"></i>
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700">Logo cửa hàng</p>
                  <p className="text-xs text-gray-400 mb-2">Hiển thị trên thanh menu bên trái</p>
                  <button
                    onClick={() => logoInputRef.current?.click()}
                    className="px-3 py-1.5 bg-gray-800 text-white rounded-lg text-xs font-semibold hover:bg-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Tải logo lên
                  </button>
                  <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                  {logoMsg && <p className="text-xs text-gray-500 mt-1.5">{logoMsg}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { label: 'Tên cửa hàng', value: shopName, onChange: setShopName, ph: 'VD: Cầm đồ ABC' },
                  { label: 'Số điện thoại', value: phone, onChange: setPhone, ph: 'VD: 0901.234.567' },
                ].map(f => (
                  <div key={f.label}>
                    <label className="text-xs text-gray-500 font-medium block mb-1">{f.label}</label>
                    <input value={f.value} onChange={e => f.onChange(e.target.value)} placeholder={f.ph} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors" />
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <label className="text-xs text-gray-500 font-medium block mb-1">Địa chỉ</label>
                  <input value={address} onChange={e => setAddress(e.target.value)} placeholder="VD: Số 123, Đường ABC, Phường X, Tỉnh Y" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors" />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">Tên cửa hàng hiển thị trên thanh menu bên trái và dùng làm mặc định trên phiếu in.</p>
            </div>

            {/* Print Info */}
            <div className="bg-white rounded-2xl p-6">
              <h3 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-red-100 rounded-lg text-red-600"><i className="ri-printer-line text-base"></i></span>
                Thông Tin Trên Hợp Đồng / Phiếu In
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="text-xs text-gray-500 font-medium block mb-1">Tên hiển thị trên hợp đồng</label>
                  <input value={printName} onChange={e => setPrintName(e.target.value)} placeholder={shopName || 'VD: Dịch vụ cầm đồ ABC'} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Địa chỉ — dòng 1</label>
                  <input value={printAddress1} onChange={e => setPrintAddress1(e.target.value)} placeholder="VD: TDP X, Phường Y" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Địa chỉ — dòng 2</label>
                  <input value={printAddress2} onChange={e => setPrintAddress2(e.target.value)} placeholder="VD: Tỉnh Z" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors" />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">Để trống sẽ tự lấy Tên cửa hàng + Địa chỉ ở trên. Máy in Bluetooth nên điền không dấu (VD: "Cam do ABC") để chữ in không bị lỗi.</p>
            </div>

            {/* Capital */}
            <div className="bg-white rounded-2xl p-6">
              <h3 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-emerald-100 rounded-lg text-emerald-600"><i className="ri-safe-2-line text-base"></i></span>
                Quỹ Vốn Kinh Doanh
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <div className="sm:col-span-2">
                  <label className="text-xs text-gray-500 font-medium block mb-1">Tổng vốn kinh doanh (VNĐ)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={capitalDisplay}
                    onChange={e => handleCapitalDisplayChange(e.target.value)}
                    placeholder="VD: 500.000.000"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                  />
                  <p className="text-xs text-gray-400 mt-1">Số vốn hiện có để tính toán vốn còn lại trên Dashboard và Báo cáo</p>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-5">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Ghi nhận giao dịch vốn</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-500 font-medium block mb-1">Loại giao dịch</label>
                    <select value={capType} onChange={e => setCapType(e.target.value as 'deposit' | 'withdraw')} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors bg-white">
                      <option value="deposit">Nhập thêm vốn</option>
                      <option value="withdraw">Rút vốn</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium block mb-1">Số tiền (VNĐ)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={capAmount}
                      onChange={e => {
                        const raw = e.target.value.replace(/\D/g, '');
                        setCapAmount(raw ? Number(raw).toLocaleString('vi-VN') : '');
                      }}
                      placeholder="VD: 50.000.000"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium block mb-1">Ghi chú</label>
                    <input type="text" value={capNote} onChange={e => setCapNote(e.target.value)} placeholder="VD: Bổ sung vốn tháng 5" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors" />
                  </div>
                </div>
                {capError && (
                  <p className="text-xs text-red-500 mb-2">{capError}</p>
                )}
                <div className="flex items-center gap-3">
                  <button onClick={handleCapitalTransaction} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap">
                    <span className="flex items-center gap-1.5">
                      <span className="w-4 h-4 flex items-center justify-center"><i className={`text-sm ${capType === 'deposit' ? 'ri-add-line' : 'ri-subtract-line'}`}></i></span>
                      {capType === 'deposit' ? 'Nhập thêm vốn' : 'Rút vốn'}
                    </span>
                  </button>
                  {capSuccess && (
                    <div className="flex items-center gap-1.5 text-emerald-600 text-sm font-medium">
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-checkbox-circle-line text-sm"></i></span>
                      Đã ghi nhận!
                    </div>
                  )}
                </div>
              </div>

              {capitalLogs.length > 0 && (
                <div className="border-t border-gray-100 pt-5 mt-5">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Lịch sử thay đổi vốn gần đây</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {capitalLogs.slice(0, 10).map(log => (
                      <div key={log.id} className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 rounded-lg">
                        <div className={`w-7 h-7 flex items-center justify-center rounded-full shrink-0 ${log.type === 'deposit' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-500'}`}>
                          <i className={`text-xs ${log.type === 'deposit' ? 'ri-add-line' : 'ri-subtract-line'}`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-700 font-medium">
                            {log.type === 'deposit' ? 'Nhập thêm vốn' : 'Rút vốn'} · {log.amount.toLocaleString('vi-VN')}đ
                          </p>
                          {log.note && <p className="text-xs text-gray-400 truncate">{log.note}</p>}
                        </div>
                        <p className="text-xs text-gray-400 shrink-0">{new Date(log.createdAt).toLocaleDateString('vi-VN')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Loan Config */}
            <div className="bg-white rounded-2xl p-6">
              <h3 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-blue-100 rounded-lg text-blue-600"><i className="ri-percent-line text-base"></i></span>
                Cấu Hình Khoản Vay
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Lãi suất mặc định (%/tháng)</label>
                  <input type="number" value={interestRate} onChange={e => setInterestRate(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Thời hạn vay mặc định (ngày)</label>
                  <input type="number" value={loanDays} onChange={e => setLoanDays(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Nhắc nhở trước hạn (ngày)</label>
                  <input type="number" value={remindDays} onChange={e => setRemindDays(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">Tỷ lệ cho vay tối đa (%)</label>
                  <input type="number" value={maxLoanRatio} onChange={e => setMaxLoanRatio(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6">
              <h3 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-orange-100 rounded-lg text-orange-600"><i className="ri-price-tag-3-line text-base"></i></span>
                Danh mục tài sản
              </h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {(categories.length ? categories : ['Vàng bạc', 'Điện tử', 'Điện thoại', 'Xe máy', 'Ô tô', 'Đồ gia dụng', 'Khác']).map(cat => (
                  <span key={cat} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 text-xs font-medium text-gray-700">
                    {cat}
                    <button type="button" onClick={() => setCategories(prev => (prev.length ? prev : ['Vàng bạc', 'Điện tử', 'Điện thoại', 'Xe máy', 'Ô tô', 'Đồ gia dụng', 'Khác']).filter(c => c !== cat))} className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer">
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={newCategory} onChange={e => setNewCategory(e.target.value)} placeholder="Thêm danh mục..." className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                <button type="button" onClick={() => { const t = newCategory.trim(); if (!t) return; setCategories(prev => { const base = prev.length ? prev : ['Vàng bạc', 'Điện tử', 'Điện thoại', 'Xe máy', 'Ô tô', 'Đồ gia dụng', 'Khác']; return base.includes(t) ? base : [...base, t]; }); setNewCategory(''); }} className="px-3 py-2 bg-gray-800 text-white rounded-lg text-sm font-semibold cursor-pointer">Thêm</button>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6">
              <h3 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-teal-100 rounded-lg text-teal-600"><i className="ri-home-gear-line text-base"></i></span>
                Danh sách kho
              </h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {(warehouses.length ? warehouses : ['Kho 1', 'Kho 2', 'Kho 3']).map(kho => (
                  <span key={kho} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-xs font-medium text-amber-700">
                    {kho}
                    <button type="button" onClick={() => setWarehouses(prev => (prev.length ? prev : ['Kho 1', 'Kho 2', 'Kho 3']).filter(k => k !== kho))} className="w-4 h-4 flex items-center justify-center text-amber-400 hover:text-red-500 cursor-pointer">
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={newWarehouse} onChange={e => setNewWarehouse(e.target.value)} placeholder="Thêm kho..." className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                <button type="button" onClick={() => { const t = newWarehouse.trim(); if (!t) return; setWarehouses(prev => { const base = prev.length ? prev : ['Kho 1', 'Kho 2', 'Kho 3']; return base.includes(t) ? base : [...base, t]; }); setNewWarehouse(''); }} className="px-3 py-2 bg-gray-800 text-white rounded-lg text-sm font-semibold cursor-pointer">Thêm</button>
              </div>
            </div>

            {/* Notifications */}
            <div className="bg-white rounded-2xl p-6">
              <h3 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-purple-100 rounded-lg text-purple-600"><i className="ri-notification-3-line text-base"></i></span>
                Thông Báo & Tự Động Hóa
              </h3>
              <div className="space-y-4">
                {[
                  { label: 'Thông báo qua email', desc: 'Gửi email khi có khoản vay mới hoặc đến hạn', value: emailNotif, onChange: () => setEmailNotif(!emailNotif) },
                  { label: 'Nhắc nợ tự động', desc: 'Tự động gửi SMS nhắc khách hàng trước ngày đến hạn', value: autoRemind, onChange: () => setAutoRemind(!autoRemind) },
                  { label: 'Sao lưu dữ liệu tự động', desc: 'Sao lưu dữ liệu hàng ngày lúc 2:00 AM', value: autoBackup, onChange: () => setAutoBackup(!autoBackup) },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{item.label}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
                    </div>
                    <Toggle value={item.value} onChange={item.onChange} />
                  </div>
                ))}
              </div>
            </div>

            {/* Data Sync */}
            <div className="bg-white rounded-2xl p-6">
              <h3 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center bg-red-100 rounded-lg text-red-600"><i className="ri-database-2-line text-base"></i></span>
                Dữ Liệu & Đồng Bộ
              </h3>
              <p className="text-xs text-gray-400 mb-4">
                Nút bên dưới sẽ tự động kiểm tra và sửa các lỗi đồng bộ giữa khoản vay, tồn kho và thông tin khách hàng.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleFixSync}
                  disabled={fixLoading}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-800 text-white rounded-lg text-sm font-semibold hover:bg-gray-900 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    {fixLoading ? <i className="ri-loader-4-line animate-spin text-sm"></i> : <i className="ri-refresh-line text-sm"></i>}
                  </span>
                  {fixLoading ? 'Đang kiểm tra...' : 'Kiểm tra & sửa lỗi đồng bộ'}
                </button>
                {fixResult && (
                  <div className="flex items-center gap-2">
                    {fixResult.errors.length === 0 ? (
                      <div className="flex items-center gap-1.5 text-emerald-600 text-sm font-medium">
                        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-checkbox-circle-line text-sm"></i></span>
                        Đã sửa {fixResult.fixedLoans} tồn kho, {fixResult.fixedCustomers} khách
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-red-500 text-sm font-medium">
                        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-error-warning-line text-sm"></i></span>
                        {fixResult.errors.length} lỗi — xem console
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Save */}
            <div className="flex items-center gap-3">
              <button onClick={handleSave} disabled={saving} className="px-6 py-2.5 bg-[#f59e0b] text-white rounded-lg text-sm font-semibold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                {saving ? 'Đang lưu...' : 'Lưu Cài Đặt'}
              </button>
              {saved && (
                <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-checkbox-circle-line"></i></span>
                  Đã lưu thành công!
                </div>
              )}
              {saveError && (
                <div className="flex items-center gap-2 text-red-500 text-sm font-medium">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-error-warning-line"></i></span>
                  {saveError}
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'staff' && <StaffManagement />}
      </div>
    </>
  );
}
